import { Container, Group, Stack } from "@mantine/core"
import {
  PaginationState,
  RowSelectionState,
  TableFeature,
} from "@tanstack/react-table"
import { Button, ErrorBox, getManualRowSelectionFeature } from "@tfg/ui"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { FindBatchUpdateOperationsResponseDto } from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.response.dto"

import { queryClient } from "~/backend/queryClient.ts"
import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { useBatchUpdateModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/store/BatchModalContext.tsx"
import { useBatchColumns } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/batch.columns.tsx"
import { ProductionSiteDisplay } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProductionSiteDisplay.tsx"
import { useFindOperationsWithEmptyFields } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useFindOperationsWithEmptyFields.ts"
import { useUpdateOperationsMutation } from "~/pages/SupplierPortal/TraceabilityRequests/hooks.ts"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type Props = {
  batchOperations: FindBatchUpdateOperationsResponseDto
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  setIsLoading: Dispatch<SetStateAction<boolean>>
}

export function BatchUpdateOperationsTable({
  batchOperations,
  pagination,
  setPagination,
  setIsLoading,
}: Props) {
  const { setBatchModalOpen, batchModalInfo, rowSelection, setRowSelection } =
    useBatchUpdateModalContext()
  const [pageCount, setPageCount] = useState<number>(0)
  const { t } = useTranslation(["traceability", "common"])
  const {
    mutate: updateOperations,
    isPending: isUpdating,
    error,
  } = useUpdateOperationsMutation(batchModalInfo?.requestId ?? 0, {
    onSuccess: async (data, variables) => {
      setBatchModalOpen(false)
      if (data.informationRequestIds) {
        await Promise.all(
          data.informationRequestIds.map(async (id) => {
            await queryClient.invalidateQueries({
              queryKey: ["listTraceabilityRequests", id],
            })
          })
        )
      } else {
        await queryClient.invalidateQueries({
          queryKey: ["listTraceabilityRequests"],
        })
      }

      showSuccessNotification({
        title: t("batchModal.batchUpdateSuccess"),
        message: t("batchModal.batchUpdateSuccessMessage", {
          count: variables.ids.length,
        }),
      })
    },
  })
  useEffect(() => {
    setPageCount(Math.ceil(batchOperations.count / pagination.pageSize))
  }, [batchOperations, pagination.pageSize])

  useEffect(() => {
    setIsLoading(isUpdating)
  }, [isUpdating, setIsLoading])

  const columns = useBatchColumns({
    batchOperations: batchOperations.operations,
    key: batchModalInfo?.key ?? "",
  }).filter(Boolean)

  const ManualRowSelectionFeature: TableFeature = getManualRowSelectionFeature(
    batchOperations.operationsIdList
  )

  const table = useCustomReactTable({
    _features: [ManualRowSelectionFeature],
    data: batchOperations.operations,
    columns,
    state: {
      pagination,
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => {
      return row.id.toString()
    },
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
  })

  const { refetch: refetchEmptyOperations } = useFindOperationsWithEmptyFields(
    batchOperations.operationsIdList,
    batchModalInfo?.key ?? "",
    batchModalInfo && batchModalInfo.update.customFieldsValues !== undefined
      ? batchModalInfo?.update.customFieldsValues[0]?.customFieldDefinitionId
      : undefined
  )
  if (error) {
    return <ErrorBox error={error} />
  }
  if (!batchModalInfo) {
    return null
  }
  return (
    <Stack>
      <Group>
        <Button
          variant="secondary"
          size="compact-sm"
          onClick={() =>
            setRowSelection(
              batchOperations.operationsIdList.reduce<RowSelectionState>(
                (acc, row) => {
                  acc[row] = true
                  return acc
                },
                {}
              )
            )
          }
        >
          {t("selectAll")}
        </Button>
        <Button
          variant="secondary"
          size="compact-sm"
          onClick={() => setRowSelection({})}
        >
          {t("deselectAll")}
        </Button>
        {batchModalInfo?.key !== "contactId" && ( // Hide select empty if it's contactId because contactId can't be empty
          <Button
            variant="secondary"
            size="compact-sm"
            onClick={async () => {
              const result = await refetchEmptyOperations()
              if (result?.data) {
                setRowSelection(
                  result.data.reduce<RowSelectionState>((acc, row) => {
                    acc[row] = true
                    return acc
                  }, {})
                )
              }
            }}
          >
            {t("batchModal.actions.selectEmpty")}
          </Button>
        )}
      </Group>
      <Container mah="80%" m={0} maw="100%">
        <CustomTable
          table={table}
          manualPaginationCount={batchOperations.count}
        />
      </Container>
      <Group
        justify="center"
        h="10vh"
        pos="sticky"
        bottom={0}
        bg="white"
        w="100%"
        style={{
          borderTop: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        {batchModalInfo.update.productionSiteId && (
          <ProductionSiteDisplay
            productionSiteId={batchModalInfo.update.productionSiteId}
          />
        )}
        <Button
          disabled={isUpdating}
          onClick={() => {
            updateOperations({
              ids: Object.keys(rowSelection).map(Number),
              update: batchModalInfo.update,
            })
          }}
        >
          {t("batchModal.applyButton", {
            type: batchModalInfo.label,
            itemCount: Object.keys(rowSelection).length,
          })}
        </Button>
      </Group>
    </Stack>
  )
}
